import { safeEvalMath } from './formatUtils';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

/**
 * Applies all client-side data transformations to a visual's query result:
 * date parts, page filter, pivot, table calculations, computed columns.
 */
export function getTransformedData({ vizId, visualResults, visuals, dateParts, pageField, pageValues, pageIndex, tableCalcs, visualConfigs, otherExpanded }) {
  const vr = visualResults[vizId] || {};
  const vis = visuals.find(v => v.id === vizId) || {};
  const rd = vis.rowDims || [];
  const cd = vis.colDim || '';
  const vm = vis.valueMeasures || [];
  if (!vr.result?.data?.length) return null;
  let data = [...vr.result.data];

  // 0. Date part extraction
  for (const [field, part] of Object.entries(dateParts)) {
    if (!part) continue;
    data = data.map(row => {
      const val = row[field];
      if (val == null) return row;
      const d = new Date(val);
      if (isNaN(d.getTime())) return row;
      let extracted;
      let sortVal;
      switch (part) {
        case 'year': extracted = d.getFullYear(); sortVal = extracted; break;
        case 'quarter': extracted = 'Q' + (Math.floor(d.getMonth() / 3) + 1); sortVal = Math.floor(d.getMonth() / 3) + 1; break;
        case 'month': extracted = MONTHS[d.getMonth()]; sortVal = d.getMonth() + 1; break;
        case 'month_year': extracted = MONTHS[d.getMonth()] + ' ' + d.getFullYear(); sortVal = d.getFullYear() * 100 + (d.getMonth() + 1); break;
        case 'month_number': extracted = d.getMonth() + 1; sortVal = extracted; break;
        case 'week': { const start = new Date(d.getFullYear(), 0, 1); const wk = Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7); extracted = 'W' + wk; sortVal = wk; break; }
        case 'day': extracted = d.getDate(); sortVal = extracted; break;
        case 'day_of_week': extracted = DAYS[d.getDay()]; sortVal = d.getDay(); break;
        case 'hour': extracted = d.getHours() + ':00'; sortVal = d.getHours(); break;
        default: extracted = val; sortVal = val;
      }
      return { ...row, [field]: extracted, ['_sort_' + field]: sortVal };
    });
    // Re-aggregate
    if (['year','quarter','month','month_year','month_number','week','day','day_of_week','hour'].includes(part)) {
      const grouped = {};
      // Group by row dims AND the color/series dim so the series breakdown
      // survives date-part re-aggregation (otherwise the pivot below collapses
      // to a single series).
      const groupDims = [...rd, ...(cd ? [cd] : [])];
      data.forEach(row => {
        const key = groupDims.map(dim => row[dim]).join('|||');
        if (!grouped[key]) grouped[key] = { ...row };
        else { vm.forEach(m => { grouped[key][m] = (grouped[key][m] || 0) + (row[m] || 0); }); }
      });
      data = Object.values(grouped);
    }
  }

  // 1. Page filter
  if (pageField && pageValues.length > 0 && pageValues[pageIndex] !== undefined) {
    data = data.filter(row => String(row[pageField]) === String(pageValues[pageIndex]));
  }

  // 2. Pivot data for stacked bars when colDim is set
  if (cd && rd.length > 0 && vm.length > 0) {
    // Resolve the series column case/whitespace-insensitively. SQL connectors
    // (Snowflake, BigQuery, …) can echo back the aliased column in a different
    // case than the semantic field name; without this the lookup misses and
    // every row collapses into a single "Other" series (one line only).
    const norm = (s) => String(s).toLowerCase().replace(/[\s_]+/g, '');
    const resolveKey = (row, name) => {
      if (name in row) return name;
      const target = norm(name);
      return Object.keys(row).find(k => norm(k) === target);
    };
    const pivoted = {};
    const seriesSet = new Set();
    data.forEach(row => {
      const xVal = rd.map(d => row[d]).join('|');
      if (!pivoted[xVal]) { pivoted[xVal] = {}; rd.forEach(d => { pivoted[xVal][d] = row[d]; }); }
      const seriesKey = resolveKey(row, cd);
      const series = String((seriesKey ? row[seriesKey] : undefined) ?? 'Other');
      seriesSet.add(series);
      vm.forEach(m => { pivoted[xVal][series] = (pivoted[xVal][series] || 0) + (row[m] || 0); });
    });
    // Union-fill: every pivoted row must carry every series column (0 when
    // absent for that x value). Otherwise a chart renderer that infers the
    // series list from the first row only draws the series present in that
    // (often partial) first row — e.g. a year that only has one line of
    // business — making the Color/Series pill appear to do nothing.
    const allSeries = Array.from(seriesSet);
    data = Object.values(pivoted).map(rec => {
      allSeries.forEach(s => { if (rec[s] === undefined) rec[s] = 0; });
      return rec;
    });

    // Top-N + "Other": when enabled, keep the N largest series (by grand
    // total across all rows) and roll the remainder into a single "Other"
    // series so charts with many series values stay readable. When the user
    // has drilled into "Other" (otherExpanded), skip bucketing so every
    // rolled-up series is shown individually.
    const topCfg = (visualConfigs[vizId] || {}).seriesTopN;
    if (topCfg?.enabled && !otherExpanded) {
      const n = Math.max(1, Number(topCfg.count) || 8);
      const dimSet = new Set(rd);
      const totals = {};
      data.forEach(row => {
        Object.keys(row).forEach(k => {
          if (dimSet.has(k)) return;
          totals[k] = (totals[k] || 0) + (Number(row[k]) || 0);
        });
      });
      const ranked = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
      if (ranked.length > n) {
        const keep = new Set(ranked.slice(0, n));
        data = data.map(row => {
          const out = {};
          let other = 0;
          Object.keys(row).forEach(k => {
            if (dimSet.has(k)) { out[k] = row[k]; return; }
            if (keep.has(k)) out[k] = row[k];
            else other += (Number(row[k]) || 0);
          });
          out['Other'] = (out['Other'] || 0) + other;
          return out;
        });
      }
    }
  }

  // 3. Table calculations
  const activeCalcs = Object.entries(tableCalcs).filter(([_, calc]) => calc && calc !== 'none');
  if (activeCalcs.length > 0) {
    const origData = [...data];
    for (const [measure, calc] of activeCalcs) {
      const mKey = cd ? undefined : measure;
      const keys = mKey ? [mKey] : Object.keys(data[0] || {}).filter(k => !rd.includes(k) && k !== cd);
      for (const key of keys) {
        switch (calc) {
          case 'running_total': { let running = 0; data = data.map(row => { running += (row[key] || 0); return { ...row, [key]: running }; }); break; }
          case 'pct_of_total': { const total = origData.reduce((s, r) => s + (r[key] || 0), 0); data = data.map(row => ({ ...row, [key]: total ? ((row[key] || 0) / total * 100) : 0 })); break; }
          case 'rank': { const vals = data.map((r, i) => ({ i, v: r[key] || 0 })).sort((a, b) => b.v - a.v); const rankMap = {}; vals.forEach((item, rank) => { rankMap[item.i] = rank + 1; }); data = data.map((row, i) => ({ ...row, [key]: rankMap[i] })); break; }
          case 'difference': { data = data.map((row, i) => ({ ...row, [key]: i === 0 ? 0 : (row[key] || 0) - (origData[i - 1]?.[key] || 0) })); break; }
          case 'pct_difference': { data = data.map((row, i) => { if (i === 0) return { ...row, [key]: 0 }; const prev = origData[i - 1]?.[key] || 0; return { ...row, [key]: prev ? (((row[key] || 0) - prev) / Math.abs(prev)) * 100 : 0 }; }); break; }
          case 'moving_avg': { const w = 3; data = data.map((row, i) => { const start = Math.max(0, i - w + 1); const slice = origData.slice(start, i + 1); return { ...row, [key]: slice.reduce((s, r) => s + (r[key] || 0), 0) / slice.length }; }); break; }
          case 'running_avg': { data = data.map((row, i) => { const slice = origData.slice(0, i + 1); return { ...row, [key]: slice.reduce((s, r) => s + (r[key] || 0), 0) / slice.length }; }); break; }
          case 'running_min': { let runMin = Infinity; data = data.map(row => { runMin = Math.min(runMin, row[key] || 0); return { ...row, [key]: runMin }; }); break; }
          case 'running_max': { let runMax = -Infinity; data = data.map(row => { runMax = Math.max(runMax, row[key] || 0); return { ...row, [key]: runMax }; }); break; }
          case 'yoy': { data = data.map((row, i) => ({ ...row, [key]: i >= 12 ? (row[key] || 0) - (origData[i - 12]?.[key] || 0) : 0 })); break; }
          case 'prior_period': { data = data.map((row, i) => ({ ...row, [key]: i > 0 ? origData[i - 1][key] || 0 : 0 })); break; }
          case 'growth_rate': { data = data.map((row, i) => { if (i === 0) return { ...row, [key]: 0 }; const prev = origData[i - 1]?.[key] || 0; return { ...row, [key]: prev ? (((row[key] || 0) - prev) / Math.abs(prev)) * 100 : 0 }; }); break; }
          case 'dense_rank': { const sorted = [...new Set(data.map(r => r[key] || 0))].sort((a, b) => b - a); const denseMap = {}; sorted.forEach((v, i) => { denseMap[v] = i + 1; }); data = data.map(row => ({ ...row, [key]: denseMap[row[key] || 0] || 0 })); break; }
          case 'ntile': { const n = 4; const sortedIdx = data.map((r, i) => ({ i, v: r[key] || 0 })).sort((a, b) => a.v - b.v); const tileMap = {}; sortedIdx.forEach((item, idx) => { tileMap[item.i] = Math.floor(idx / (data.length / n)) + 1; }); data = data.map((row, i) => ({ ...row, [key]: Math.min(tileMap[i] || 1, n) })); break; }
          case 'percentile_rank': { const sortedForPct = data.map((r, i) => ({ i, v: r[key] || 0 })).sort((a, b) => a.v - b.v); const pctMap = {}; sortedForPct.forEach((item, idx) => { pctMap[item.i] = ((idx + 1) / data.length * 100); }); data = data.map((row, i) => ({ ...row, [key]: Math.round(pctMap[i] || 0) })); break; }
          case 'rolling_sum_3': { data = data.map((row, i) => { const start = Math.max(0, i - 2); return { ...row, [key]: origData.slice(start, i + 1).reduce((s, r) => s + (r[key] || 0), 0) }; }); break; }
          case 'index_to_first': { const first = origData[0]?.[key] || 1; data = data.map(row => ({ ...row, [key]: first ? ((row[key] || 0) / first * 100) : 0 })); break; }
          case 'zscore': { const mean = origData.reduce((s, r) => s + (r[key] || 0), 0) / origData.length; const std = Math.sqrt(origData.reduce((s, r) => s + Math.pow((r[key] || 0) - mean, 2), 0) / origData.length); data = data.map(row => ({ ...row, [key]: std ? ((row[key] || 0) - mean) / std : 0 })); break; }
          default: break;
        }
      }
    }
  }

  // 4. Computed columns
  const vizCfg = visualConfigs[vizId] || {};
  const computedCols = vizCfg.computedColumns || [];
  if (computedCols.length > 0) {
    data = data.map(row => {
      const newRow = { ...row };
      for (const cc of computedCols) {
        if (!cc.name || !cc.formula) continue;
        try {
          let expr = cc.formula;
          for (const [k, v] of Object.entries(row)) {
            if (typeof v === 'number') {
              expr = expr.replace(new RegExp(`\\b${k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), String(v));
            }
          }
          const result = safeEvalMath(expr);
          newRow[cc.name] = typeof result === 'number' && isFinite(result) ? Math.round(result * 100) / 100 : 0;
        } catch { newRow[cc.name] = 0; }
      }
      return newRow;
    });
  }

  return data;
}
